// Port of Flutter's fanTouchScreen.dart (DeviceID 53).
//
// A 270° dial with the gap at the BOTTOM — it starts at 135° (down-left) and
// sweeps clockwise to 45° (down-right) — with the spinning blades in the middle
// and the speed readout beneath the hub.
//
// Two behaviours worth keeping in mind, both lifted from the Dart:
//
//  • Dragging moves the UI ONLY. Exactly one command is published, on release.
//    The older slider published on every drag tick and machine-gunned the device
//    with MQTT frames for a single gesture.
//  • The hub area (inner 22%) is dead to touch, so grabbing the fan graphic
//    itself doesn't fling the speed.
//
// Long-pressing the body opens the schedule for output 1, as in the Dart.

import React, {
  useCallback, useEffect, useMemo, useRef, useState,
} from 'react';
import { useContentHeight } from '../../components/MobileShell';
import { Box, Typography, useTheme } from '@mui/material';
import { useNavigate } from 'react-router-dom';
import DeviceAppBar from './components/DeviceAppBar';
import FanGauge, { DIAL_START, DIAL_SWEEP } from './components/FanGauge';
import useLongPress from '../../components/useLongPress';
import { useApp } from '../../../../core/state/AppContext';
import { publishCommand } from '../../../../core/utils/deviceController';
import {
  subscribeTopic, unsubscribeTopic, addMessageListener,
} from '../../../../core/mqtt/mqttClient';
import { fetchSeedStatus } from '../../../../core/devices/deviceStatus';
import {
  FAN_SPEEDS, fanCommand, fanFraction, fanStatusLabel, fanStepLabel, parseFanSpeed,
} from '../../../../core/devices/fan';

// DIAL_START / DIAL_SWEEP come from FanGauge — the geometry the gesture maps
// onto has to be the geometry that gets drawn, or the knob lands where the
// finger did not.

export default function FanScreen({ device }) {
  const theme = useTheme();
  const contentH = useContentHeight();
  const c = theme.sli;
  const navigate = useNavigate();
  const { userId } = useApp();
  const uno = device?.UniqueNumber;

  const [speed, setSpeed] = useState(FAN_SPEEDS[0]);
  const [known, setKnown] = useState(false);
  const [dragSpeed, setDragSpeed] = useState(null);
  const boxRef = useRef(null);

  const displaySpeed = dragSpeed ?? speed;

  // Seed from the server, then follow MQTT.
  useEffect(() => {
    if (!uno) return undefined;
    let alive = true;
    fetchSeedStatus(uno).then((raw) => {
      const s = parseFanSpeed(raw);
      if (alive && s != null) { setSpeed(s); setKnown(true); }
    });
    return () => { alive = false; };
  }, [uno]);

  useEffect(() => {
    if (!uno) return undefined;
    const topic = `${uno}/client`;
    subscribeTopic(topic);
    const off = addMessageListener((t, payload) => {
      if (t !== topic) return;
      const open = payload.indexOf('[');
      const close = payload.indexOf(']');
      if (open === -1 || close === -1) return;
      if (payload.substring(open + 1, close) !== String(uno)) return;
      const s = parseFanSpeed(payload);
      if (s != null) { setSpeed(s); setKnown(true); }
    });
    return () => { off(); unsubscribeTopic(topic); };
  }, [uno]);

  const box = useMemo(() => {
    const availW = window.innerWidth;
    // ⚠️ THE CONTENT AREA, NOT THE WINDOW. The window also holds the identity
    // strip and the company footer, which this screen does not get — sizing
    // from it overflows by exactly their height, and the overflow is clipped
    // rather than scrolled. See ui/mobile/components/MobileShell.
    const availH = contentH - 64;
    return Math.min(380, Math.max(220, Math.min(availW * 0.82, availH * 0.62)));
  }, [contentH]);

  // The knob/hub colour moved into FanGauge with the rest of the drawing.
  const running = known && displaySpeed > 10;
  const frac = fanFraction(displaySpeed);

  // Map a touch inside the dial to a speed (drag only — nothing published here).
  const handleTouch = useCallback((clientX, clientY) => {
    const el = boxRef.current;
    if (!el) return;
    const rect = el.getBoundingClientRect();
    const dx = clientX - rect.left - box / 2;
    const dy = clientY - rect.top - box / 2;
    if (Math.hypot(dx, dy) < box * 0.22) return; // dead hub

    let t = (Math.atan2(dy, dx) - DIAL_START) % (2 * Math.PI);
    if (t < 0) t += 2 * Math.PI;
    if (t > DIAL_SWEEP) {
      // In the gap at the bottom — snap to whichever end is nearer.
      t = (t - DIAL_SWEEP) < (2 * Math.PI - t) ? DIAL_SWEEP : 0;
    }
    const idx = Math.round((t / DIAL_SWEEP) * (FAN_SPEEDS.length - 1));
    setDragSpeed(FAN_SPEEDS[Math.min(FAN_SPEEDS.length - 1, Math.max(0, idx))]);
  }, [box]);

  // Publish the speed the finger landed on — exactly one message per gesture.
  const commit = useCallback(() => {
    if (dragSpeed == null) return;
    setSpeed(dragSpeed);
    setKnown(true);
    publishCommand(uno, fanCommand(dragSpeed), userId);
    setDragSpeed(null);
  }, [dragSpeed, uno, userId]);

  const bodyPress = useLongPress(
    () => navigate(`/schedule/${uno}/1`, { state: { outputName: device?.DeviceName, device } }),
    null,
  );

  return (
    <Box sx={{ height: '100%', overflowY: 'auto', bgcolor: c.back, display: 'flex', flexDirection: 'column' }}>
      <DeviceAppBar device={device} />

      <Box
        {...bodyPress}
        sx={{
          flex: 1, display: 'flex', flexDirection: 'column',
          alignItems: 'center', justifyContent: 'center', p: 2,
        }}
      >
        <Box
          ref={boxRef}
          sx={{ width: box, height: box, position: 'relative', touchAction: 'none' }}
          onPointerDown={(e) => { e.currentTarget.setPointerCapture(e.pointerId); handleTouch(e.clientX, e.clientY); }}
          onPointerMove={(e) => { if (e.buttons) handleTouch(e.clientX, e.clientY); }}
          onPointerUp={commit}
          onPointerCancel={commit}
        >
          {/* The dial itself lives in FanGauge — the BLE bench panel draws the
              same one for a 7+1 / 7+2's fan channels, and two copies of this
              geometry would drift the way the button order once did. What stays
              here is everything about INPUT: the dead hub, drag-then-commit, and
              publishing exactly one command per gesture. */}
          <FanGauge
            size={box}
            frac={frac}
            running={running}
            known={known}
            stepLabel={fanStepLabel(displaySpeed)}
            statusLabel={fanStatusLabel(displaySpeed)}
          />
        </Box>

        <Box sx={{ height: box * 0.05 }} />
        <Typography sx={{
          fontSize: Math.min(15, Math.max(11, box * 0.045)), color: `${c.text}99`,
        }}
        >
          Drag around the dial to set speed
        </Typography>
      </Box>
    </Box>
  );
}
